
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBagImageUpload } from "@/hooks/useBagImageUpload";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";

interface BagImageUploadProps {
  styleNumber: string;
}

export const BagImageUpload = ({ styleNumber }: BagImageUploadProps) => {
  const { uploadImages, isUploading, error } = useBagImageUpload();
  const { toast } = useToast();
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setMainImageFile(e.target.files[0]);
    }
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAdditionalImageFiles(Array.from(e.target.files));
    }
  };

  const handleUpload = async () => {
    if (!styleNumber) {
      toast({
        title: "Error",
        description: "Style number is required for upload",
        variant: "destructive",
      });
      return;
    }

    try {
      await uploadImages(
        parseInt(styleNumber),
        mainImageFile || undefined,
        additionalImageFiles.length > 0 ? additionalImageFiles : undefined
      );

      toast({
        title: "Success",
        description: "Images uploaded successfully",
      });

      setMainImageFile(null);
      setAdditionalImageFiles([]);
    } catch (err) {
      toast({
        title: "Error",
        description: error || "Failed to upload images",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4 border-t pt-4">
      <h3 className="text-lg font-semibold">Upload Images</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-muted-foreground mb-2">
            Main Image
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleMainImageChange}
            className="block w-full text-sm text-slate-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-gold/10 file:text-gold
              hover:file:bg-gold/20"
          />
        </div>
        
        <div>
          <label className="block text-sm text-muted-foreground mb-2">
            Additional Images
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleAdditionalImagesChange}
            className="block w-full text-sm text-slate-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-gold/10 file:text-gold
              hover:file:bg-gold/20"
          />
        </div>

        <Button
          onClick={handleUpload}
          disabled={isUploading || (!mainImageFile && additionalImageFiles.length === 0)}
          className="w-full"
        >
          {isUploading ? (
            "Uploading..."
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Images
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
